import type React from 'react';
import { contrastTextColorForBackground, isLightBackgroundHex } from './contrastText';

/** 흐르는 그라데이션 배경 — `bg_color_by_field` 값은 `anim:<id>` */
export const PROGRAM_CELL_BG_ANIM_PRESETS = [
  { id: 'red-yellow', label: '빨강-노랑' },
  { id: 'yellow-green', label: '노랑-초록' },
  { id: 'green-blue', label: '초록-파랑' },
  { id: 'blue-purple', label: '파랑-보라' },
  { id: 'purple-magenta', label: '보라-자주' },
  { id: 'rainbow', label: '무지개' },
] as const;

export type ProgramCellBgAnimId = (typeof PROGRAM_CELL_BG_ANIM_PRESETS)[number]['id'];

const ANIM_PREFIX = 'anim:';
const ANIM_IDS = new Set<string>(PROGRAM_CELL_BG_ANIM_PRESETS.map((p) => p.id));
const HEX_RE = /^#[0-9a-f]{6}$/;

export function programCellBgAnimToken(id: ProgramCellBgAnimId): string {
  return `${ANIM_PREFIX}${id}`;
}

export function parseProgramCellBgAnimId(value: string | null | undefined): ProgramCellBgAnimId | null {
  const v = (value ?? '').trim().toLowerCase();
  if (!v.startsWith(ANIM_PREFIX)) return null;
  const id = v.slice(ANIM_PREFIX.length);
  return ANIM_IDS.has(id) ? (id as ProgramCellBgAnimId) : null;
}

export function programCellBgAnimLabel(value: string | null | undefined): string | null {
  const id = parseProgramCellBgAnimId(value);
  return id ? PROGRAM_CELL_BG_ANIM_PRESETS.find((p) => p.id === id)?.label ?? null : null;
}

/** hex(#rrggbb) 또는 애니메이션 토큰만 통과 */
export function normalizeProgramCellBgValue(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  let s = raw.trim().toLowerCase();
  const animId = parseProgramCellBgAnimId(s);
  if (animId) return programCellBgAnimToken(animId);
  if (/^#[0-9a-f]{3}$/.test(s)) s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  return HEX_RE.test(s) ? s : null;
}

/** 셀(td)에 붙일 배경 클래스·스타일 */
export function programCellBgRenderProps(value: string | null | undefined): {
  className: string;
  style: React.CSSProperties | undefined;
} {
  const v = (value ?? '').trim();
  if (!v) return { className: '', style: undefined };
  const animId = parseProgramCellBgAnimId(v);
  if (animId) {
    /* 흐르는 그라데이션 프리셋은 모두 밝은 파스텔 → 다크 모드에서도 검정 글자 */
    return { className: `cp-program-cell-bg-anim cp-program-cell-bg-anim--${animId} cp-on-light-bg`, style: undefined };
  }
  /* 사용자 지정 배경은 다크 모드에서도 그대로 칠해지므로, 밝은 배경이면 글자를 검정으로(다크 전용 CSS: .cp-on-light-bg) */
  const onLight = isLightBackgroundHex(v);
  return {
    className: onLight ? 'cp-program-progress-cell-custom-bg cp-on-light-bg' : 'cp-program-progress-cell-custom-bg',
    style: { '--cp-program-cell-bg': v, '--cp-on-bg': contrastTextColorForBackground(v) } as React.CSSProperties,
  };
}

/** 원형 스와치 안쪽 요소용 (애니메이션은 클래스, 단색은 backgroundColor) */
export function programCellBgSwatchProps(value: string): { className: string; style: React.CSSProperties | undefined } {
  const animId = parseProgramCellBgAnimId(value);
  if (animId) return { className: `cp-program-cell-bg-anim cp-program-cell-bg-anim--${animId}`, style: undefined };
  return { className: '', style: { backgroundColor: value } };
}
